import React, { useState, useRef, useEffect } from "react";
import DropDownArrow from "./DropDownArrow";

interface CustomSelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string;
  height?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  dropDownStyle?: React.CSSProperties;
  textSize?: string;
  isCityDropdown?: boolean;
  columns?: number; // New prop for flexible column control
  mobileColumns?: number; // Columns for mobile view
  desktopColumns?: number; // Columns for desktop view
  maxHeight?: string; // New prop for dropdown max height
  debug?: boolean; // Debug prop to force scrollbar visibility
  iconOpacity?: number; // Opacity for dropdown icon (0-1)
  iconSize?: string; // Size class for dropdown icon (e.g., "w-4 h-4")
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = "Select...",
  width = "280px",
  height = "48px",
  className = "",
  style = {},
  disabled = false,
  dropDownStyle = {},
  textSize = "16px",
  isCityDropdown = false,
  columns,
  mobileColumns,
  desktopColumns,
  maxHeight = "300px",
  debug = false,
  iconOpacity = 1,
  iconSize = "w-5 h-5",
}) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    }
    if (openDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // Calculate dropdown position synchronously (avoids jump: open once at correct position)
  const getDropdownPosition = (): 'top' | 'bottom' => {
    if (!dropdownRef.current) return 'bottom';
    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 300;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) return 'top';
    return 'bottom';
  };

  const selectedOption = options.find((opt) => opt.value === value);

  // Determine number of columns based on props
  const getColumnClass = () => {
    // If mobileColumns and desktopColumns are provided, use them
    if (mobileColumns !== undefined && desktopColumns !== undefined) {
      return `grid-cols-${mobileColumns} md:grid-cols-${desktopColumns}`;
    }
    // If only mobileColumns is provided, use it for all screen sizes
    if (mobileColumns !== undefined) {
      return `grid-cols-${mobileColumns}`;
    }
    // If only desktopColumns is provided, use 1 for mobile and desktopColumns for desktop
    if (desktopColumns !== undefined) {
      return `grid-cols-1 md:grid-cols-${desktopColumns}`;
    }
    // Fallback to columns prop (backward compatibility)
    if (columns) {
      // For time selection: 1 column on mobile, 2 columns on desktop
      if (columns === 1) {
        return "grid-cols-1 md:grid-cols-2";
      }
      return `grid-cols-${columns}`;
    }
    if (isCityDropdown) {
      return "grid-cols-2";
    }
    return "grid-cols-3";
  };

  return (
    <div
      className={`relative w-[${width}] ${className} rounded-full flex items-center justify-between`}
      style={{ width:width,height:height, ...style }}
      ref={dropdownRef}
    >
      <div
        className={`rounded-3xl flex items-center justify-between w-full px-4 cursor-pointer ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        onClick={() => {
          if (!disabled) {
            const newOpenState = !openDropdown;
            if (newOpenState) {
              setDropdownPosition(getDropdownPosition());
              setOpenDropdown(true);
            } else {
              setOpenDropdown(false);
            }
          }
        }}
      >
        
        <p className={`${textSize? `text-[${textSize}]` : "text-[14px] lg:text-[16px]"} text-black font-medium`}>
          {selectedOption?.label || placeholder}
        </p>
       
        <DropDownArrow openDropdown={openDropdown} iconOpacity={iconOpacity} iconSize={iconSize}/>
      </div>
      {openDropdown && (
        <div
          className={`absolute w-full bg-[#4087DF] border border-gray-200 rounded-xl p-3 z-[99999] shadow-lg ${
            dropdownPosition === 'top' ? 'bottom-12' : 'top-12'
          }`}
          style={{...dropDownStyle}}
        >
          <div 
            className={`w-full custom-select-scrollbar`} 
            style={{ 
              maxHeight: debug ? '150px' : maxHeight, 
              overflowY: 'auto', 
              overflowX: 'hidden'
            }}
          >
            <div className={`grid ${getColumnClass()} gap-2`}>
              {options.map((opt, index) => (
                <div
                  key={`${opt.value}-${index}`}
                  onClick={() => {
                    setOpenDropdown(false);
                    onChange(opt.value);
                  }}
                  className={`px-2 md:px-1 lg:px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 text-center border ${
                    value === opt.value
                      ? "bg-[#F05A24] text-white border-[#F05A24] shadow-md"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                >
                  <span className={`${textSize ? `text-[${textSize}]` : "text-[14px]"} font-medium`}>{opt.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect; 